import React, { useContext } from 'react';
import { bool, func, oneOf } from 'prop-types';
import classNames from 'classnames/bind';
import { Placeholder, Rating } from '@thd-olt-component-react/core-ui';
import { Typography } from '@one-thd/sui-atomic-components';
import {
  arrayOf,
  extend,
  number,
  params,
  shape,
  string as stringType,
  useDataModel
} from '@thd-nucleus/data-sources';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { Tooltip } from '@thd-olt-component-react/tooltip';
import { StorePodContext } from '../StorePod.context';
import { RatingDetailed } from './RatingDetailed';

const cx = classNames.bind();

export const StorePodRating = ({ color, disableTooltip, onClick }) => {
  const { loading, store } = useContext(StorePodContext);
  const { channel } = useContext(ExperienceContext);

  const { storeId } = store || {};
  const options = {
    variables: {
      storeId
    },
    skip: !storeId
  };

  const { data, error } = useDataModel('storeReviews', options);

  const {
    AverageOverallRating: averageOverallRating,
    NotRecommendedCount: notRecommendedCount,
    RatingDistribution: ratingDistribution,
    RecommendedCount: recommendedCount,
    TotalReviewCount: totalReviewCount
  } = data?.storeReviews?.Includes?.Products?.store?.FilteredReviewStatistics || {};

  const isLoading = loading || !totalReviewCount || !averageOverallRating;

  if (isLoading) {
    return (
      <div data-testid="store-pod-rating-loader">
        <Placeholder width="100px" height="21px" />
      </div>
    );
  }

  const HoverContent = React.forwardRef(function Content(props, ref) {
    return (
      <RatingDetailed
        averageOverallRating={averageOverallRating}
        notRecommendedCount={notRecommendedCount}
        ratingDistribution={ratingDistribution}
        recommendedCount={recommendedCount}
        totalReviewCount={totalReviewCount}
      />
    );
  });

  // inline style necessary to override deprecated margin values

  return (
    <button onClick={onClick} style={{ marginBottom: '-15px' }} type="submit">
      <Tooltip
        channel={channel}
        className={cx('store-pod-rating__tooltip')}
        content={<HoverContent />}
        disableTooltip={disableTooltip}
        interactive
        placement="bottom-start"
      >
        <div className="sui-flex" data-testid="store-pod-rating">
          <Rating value={Number(averageOverallRating)} />
          <Typography color={color} height="snug" weight="regular" variant="body-xs">({totalReviewCount})</Typography>
        </div>
      </Tooltip>
    </button>
  );
};

StorePodRating.propTypes = {
  color: oneOf(['primary', 'inverse']),
  disableTooltip: bool,
  onClick: func
};

StorePodRating.defaultProps = {
  color: 'primary',
  disableTooltip: false,
  onClick: () => {}
};

StorePodRating.displayName = 'StorePodRating';

StorePodRating.dataModel = extend({
  storeReviews: params({
    storeId: stringType().isRequired()
  }).shape({
    Includes: shape({
      Products: shape({
        store: shape({
          FilteredReviewStatistics: shape({
            AverageOverallRating: stringType(),
            NotRecommendedCount: number(),
            RatingDistribution: arrayOf(shape({
              RatingValue: number(),
              Count: number(),
            })),
            RecommendedCount: number(),
            TotalReviewCount: number()
          }),
        })
      })
    })
  })
});
